import { useState, useEffect, useCallback } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiClient } from '@/lib/api';
import { Section } from '@/lib/types';
import LessonManager from './LessonManager';

interface SectionManagerProps {
    courseId: number;
}

function SortableSection({
    section,
    isSelected,
    onSelect,
    onEdit,
    onDelete,
    onClone,
}: {
    section: Section;
    isSelected: boolean;
    onSelect: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onClone: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
            onClick={onSelect}
            className={`rounded-xl border-2 bg-white p-3 cursor-pointer transition-colors ${
                isSelected ? 'border-blue-500 shadow-sm' : 'border-slate-200 hover:border-slate-300'
            }`}
        >
            <div className="flex items-center gap-2">
                <button
                    {...attributes}
                    {...listeners}
                    onClick={e => e.stopPropagation()}
                    className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 p-1 rounded shrink-0"
                    title="Drag to reorder"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{section.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                        #{section.order} · {section.lessons_count ?? 0} lesson{section.lessons_count !== 1 ? 's' : ''}
                    </p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onEdit(); }}
                        className="p-1.5 rounded-lg text-amber-700 hover:bg-amber-50 transition-colors"
                        title="Rename"
                        aria-label="Rename"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onClone(); }}
                        className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                        title="Clone"
                        aria-label="Clone"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onDelete(); }}
                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete"
                        aria-label="Delete"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function SectionManager({ courseId }: SectionManagerProps) {
    const [sections, setSections] = useState<Section[]>([]);
    const [selectedSection, setSelectedSection] = useState<Section | null>(null);
    const [loading, setLoading] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingSection, setEditingSection] = useState<Section | null>(null);
    const [formTitle, setFormTitle] = useState('');
    const [sectionsCollapsed, setSectionsCollapsed] = useState(() => {
        try {
            return sessionStorage.getItem('course-editor-sections-collapsed') === '1';
        } catch {
            return false;
        }
    });

    const setCollapsed = (value: boolean) => {
        setSectionsCollapsed(value);
        try {
            sessionStorage.setItem('course-editor-sections-collapsed', value ? '1' : '0');
        } catch {
            /* ignore quota / private mode */
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const loadSections = useCallback(async () => {
        setLoading(true);
        const res = await apiClient.getSections(courseId);
        if (res.success && res.data) {
            const items = Array.isArray(res.data) ? res.data : (res.data as any)?.results || [];
            setSections(items.filter((s: Section) => s.course === courseId).sort((a: Section, b: Section) => a.order - b.order));
        } else {
            setError('Failed to load sections');
        }
        setLoading(false);
    }, [courseId]);

    useEffect(() => { loadSections(); }, [loadSections]);

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = sections.findIndex(s => s.id === active.id);
        const newIndex = sections.findIndex(s => s.id === over.id);
        const reordered = arrayMove(sections, oldIndex, newIndex);

        setSections(reordered);
        setReordering(true);

        const res = await apiClient.reorderSections(reordered.map(s => s.id));
        setReordering(false);

        if (!res.success) {
            setError('Failed to save new order. Reloading…');
            loadSections();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formTitle.trim()) return;
        setError(null);

        if (editingSection) {
            const res = await apiClient.updateSection(editingSection.id, { title: formTitle });
            if (res.success) {
                loadSections();
                resetForm();
            } else {
                setError('Failed to update section');
            }
        } else {
            const res = await apiClient.createSection({ course: courseId, title: formTitle, order: sections.length + 1 });
            if (res.success) {
                loadSections();
                resetForm();
            } else {
                setError('Failed to create section');
            }
        }
    };

    const handleDelete = async (section: Section) => {
        if (!confirm(`Delete section "${section.title}" and all its lessons?`)) return;
        const res = await apiClient.deleteSection(section.id);
        if (res.success) {
            if (selectedSection?.id === section.id) setSelectedSection(null);
            loadSections();
        } else {
            setError('Failed to delete section');
        }
    };

    const handleClone = async (section: Section) => {
        const res = await apiClient.cloneSection(section.id);
        if (res.success) loadSections();
        else setError(res.error || 'Failed to clone section');
    };

    const startEdit = (section: Section) => {
        setEditingSection(section);
        setFormTitle(section.title);
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingSection(null);
        setFormTitle('');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row lg:items-stretch">
            {sectionsCollapsed ? (
                <div className="flex w-full shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-2 lg:h-full lg:w-10 lg:flex-col">
                    <button
                        type="button"
                        onClick={() => setCollapsed(false)}
                        className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                        title="Show sections"
                        aria-label="Show sections"
                    >
                        <svg className="w-4 h-4 rotate-90 lg:rotate-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                    {selectedSection && (
                        <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-[11px] font-semibold text-blue-700"
                            title={selectedSection.title}
                        >
                            {selectedSection.order}
                        </span>
                    )}
                </div>
            ) : (
                <div className="flex h-full min-h-0 w-full shrink-0 flex-col lg:w-96">
                    <div className="mb-4 flex shrink-0 items-center justify-between gap-2">
                        <div className="flex items-center gap-1 min-w-0">
                            <button
                                type="button"
                                onClick={() => setCollapsed(true)}
                                className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                title="Hide sections"
                                aria-label="Hide sections"
                            >
                                <svg className="w-4 h-4 rotate-90 lg:rotate-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <h3 className="text-base font-semibold text-slate-900 truncate">
                                Sections <span className="text-slate-400 font-normal text-sm">({sections.length})</span>
                            </h3>
                        </div>
                        <button
                            onClick={() => { resetForm(); setShowForm(true); }}
                            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors shrink-0"
                        >
                            + Add Section
                        </button>
                    </div>

                    {error && (
                        <div className="mb-3 shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {error}
                            <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
                        </div>
                    )}

                    {showForm && (
                        <form onSubmit={handleSubmit} className="mb-4 shrink-0 rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                                {editingSection ? 'Rename Section' : 'Section Title'}
                            </label>
                            <input
                                autoFocus
                                type="text"
                                value={formTitle}
                                onChange={e => setFormTitle(e.target.value)}
                                placeholder="e.g. Introduction to Python"
                                required
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="mt-3 flex gap-2">
                                <button type="submit" className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">
                                    {editingSection ? 'Update' : 'Create'}
                                </button>
                                <button type="button" onClick={resetForm} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}

                    {reordering && (
                        <p className="mb-2 shrink-0 animate-pulse text-xs text-slate-500">Saving order…</p>
                    )}

                    {sections.length === 0 ? (
                        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
                            <p className="text-sm text-slate-400">No sections yet.</p>
                            <p className="mt-1 text-xs text-slate-300">Add your first section to get started.</p>
                        </div>
                    ) : (
                        <div className="min-h-0 max-h-[40vh] flex-1 overflow-y-auto pr-1 lg:max-h-none">
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                                    <div className="space-y-2">
                                        {sections.map(section => (
                                        <SortableSection
                                            key={section.id}
                                            section={section}
                                            isSelected={selectedSection?.id === section.id}
                                            onSelect={() => setSelectedSection(section)}
                                            onEdit={() => startEdit(section)}
                                            onDelete={() => handleDelete(section)}
                                            onClone={() => handleClone(section)}
                                        />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        </div>
                    )}
                </div>
            )}

            <div className="h-full min-h-0 min-w-0 flex-1 overflow-hidden">
                {selectedSection ? (
                    <LessonManager
                        courseId={courseId}
                        sectionId={selectedSection.id}
                        sectionTitle={selectedSection.title}
                        sections={sections}
                        onLessonsChange={() => loadSections()}
                    />
                ) : (
                    <div className="flex h-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-20 text-center">
                        <p className="text-sm text-slate-400">Select a section to manage its lessons</p>
                    </div>
                )}
            </div>
        </div>
    );
}
